import React, { useEffect, useMemo, useRef } from 'react';
import {
  Calendar,
  type DateSelectArg,
  type DatesSetArg,
  type EventDropArg,
  type EventInput,
  type EventResizeDoneArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import type { EventRecord, TaskRecord } from '../api';
import type { EventEditorDraft } from './EventDetail';
import { formatDateKey } from '../utils/date';

interface Props {
  tasks: TaskRecord[];
  events: EventRecord[];
  onRequestCreateEvent: (draft: EventEditorDraft) => void;
  onSelectTask: (task: TaskRecord) => void;
  onSelectEvent: (event: EventRecord) => void;
  onMoveEvent: (event: EventRecord, updates: { event_date: string; start_time: string | null; end_time: string | null; all_day: boolean }) => Promise<void>;
  onMoveTask: (task: TaskRecord, updates: { due_date: string; scheduled_start: string | null; scheduled_end: string | null }) => Promise<void>;
  onVisibleRangeChange: (range: { start: string; end: string }) => void;
}

export function CalendarView({
  tasks,
  events,
  onRequestCreateEvent,
  onSelectTask,
  onSelectEvent,
  onMoveEvent,
  onMoveTask,
  onVisibleRangeChange,
}: Props) {
  const calRef = useRef<HTMLDivElement>(null);
  const calInstance = useRef<Calendar | null>(null);
  const createEventRef = useRef(onRequestCreateEvent);
  const selectTaskRef = useRef(onSelectTask);
  const selectEventRef = useRef(onSelectEvent);
  const moveEventRef = useRef(onMoveEvent);
  const moveTaskRef = useRef(onMoveTask);
  const visibleRangeRef = useRef(onVisibleRangeChange);

  useEffect(() => {
    createEventRef.current = onRequestCreateEvent;
    selectTaskRef.current = onSelectTask;
    selectEventRef.current = onSelectEvent;
    moveEventRef.current = onMoveEvent;
    moveTaskRef.current = onMoveTask;
    visibleRangeRef.current = onVisibleRangeChange;
  }, [onMoveEvent, onMoveTask, onRequestCreateEvent, onSelectEvent, onSelectTask, onVisibleRangeChange]);

  const calendarEvents = useMemo(() => buildCalendarEvents(tasks, events), [tasks, events]);

  useEffect(() => {
    if (!calRef.current || calInstance.current) {
      return;
    }

    const calendar = new Calendar(calRef.current, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,listWeek',
      },
      events: [],
      editable: true,
      selectable: true,
      selectMirror: true,
      eventResizableFromStart: true,
      dayMaxEvents: 3,
      nowIndicator: true,
      height: '100%',
      dateClick: (info) => {
        createEventRef.current({
          title: '',
          description: '',
          eventDate: info.dateStr,
          startTime: '',
          endTime: '',
          allDay: true,
          color: '#3b82f6',
          taskId: null,
        });
      },
      select: (info: DateSelectArg) => {
        createEventRef.current(buildDraftFromSelection(info));
      },
      eventClick: (info) => {
        const data = info.event.extendedProps.data as TaskRecord | EventRecord;
        if (info.event.extendedProps.type === 'task') {
          selectTaskRef.current(data as TaskRecord);
          return;
        }
        selectEventRef.current(data as EventRecord);
      },
      eventDrop: (info) => {
        void handleCalendarMove(info, moveEventRef.current, moveTaskRef.current);
      },
      eventResize: (info: EventResizeDoneArg) => {
        void handleCalendarMove(info, moveEventRef.current, moveTaskRef.current);
      },
      datesSet: (info: DatesSetArg) => {
        visibleRangeRef.current(toInclusiveRange(info));
      },
    });

    calendar.render();
    calInstance.current = calendar;

    return () => {
      calendar.destroy();
      calInstance.current = null;
    };
  }, []);

  useEffect(() => {
    const calendar = calInstance.current;
    if (!calendar) {
      return;
    }

    calendar.removeAllEvents();
    calendarEvents.forEach((event) => {
      calendar.addEvent(event);
    });
  }, [calendarEvents]);

  return <div ref={calRef} style={{ height: '100%' }} />;
}

function buildCalendarEvents(tasks: TaskRecord[], events: EventRecord[]): EventInput[] {
  const priorityColors: Record<number, string> = {
    1: '#ef4444',
    2: '#f59e0b',
    3: '#3b82f6',
    4: '#6b7280',
  };

  const calendarEvents: EventInput[] = events.map((event) => ({
    id: `event-${event.id}`,
    title: event.title,
    start: event.allDay ? event.eventDate : `${event.eventDate}T${event.startTime || '00:00'}`,
    end: event.endTime ? `${event.eventDate}T${event.endTime}` : undefined,
    allDay: event.allDay,
    color: event.color || '#3b82f6',
    extendedProps: { type: 'event', data: event },
  }));

  tasks.forEach((task) => {
    if (!task.dueDate && !task.scheduledStart) {
      return;
    }

    if (task.scheduledStart && task.scheduledEnd) {
      calendarEvents.push({
        id: `task-scheduled-${task.id}`,
        title: `📌 ${task.title}`,
        start: task.scheduledStart,
        end: task.scheduledEnd,
        color: priorityColors[task.priority] || '#3b82f6',
        extendedProps: { type: 'task', data: task },
      });
      return;
    }

    if (!task.dueDate) {
      return;
    }

    calendarEvents.push({
      id: `task-${task.id}`,
      title: `${task.status === 'done' ? '✓' : '○'} ${task.title}`,
      start: task.dueDate,
      allDay: true,
      color: task.status === 'done' ? '#22c55e' : (priorityColors[task.priority] || '#3b82f6'),
      display: 'block',
      extendedProps: { type: 'task', data: task },
      classNames: task.status === 'done' ? ['fc-event-done'] : [],
    });
  });

  return calendarEvents;
}

async function handleCalendarMove(
  info: EventDropArg | EventResizeDoneArg,
  onMoveEvent: Props['onMoveEvent'],
  onMoveTask: Props['onMoveTask'],
) {
  const start = info.event.start;
  const end = info.event.end;
  const eventType = info.event.extendedProps.type as 'event' | 'task';

  if (!start) {
    info.revert();
    return;
  }

  try {
    if (eventType === 'event') {
      const data = info.event.extendedProps.data as EventRecord;
      await onMoveEvent(data, {
        event_date: formatDateKey(start),
        start_time: info.event.allDay ? null : toTimeLabel(start),
        end_time: info.event.allDay || !end ? null : toTimeLabel(end),
        all_day: info.event.allDay,
      });
      return;
    }

    const data = info.event.extendedProps.data as TaskRecord;
    await onMoveTask(data, {
      due_date: formatDateKey(start),
      scheduled_start: info.event.allDay ? null : start.toISOString(),
      scheduled_end: info.event.allDay || !end ? null : end.toISOString(),
    });
  } catch {
    info.revert();
  }
}

function buildDraftFromSelection(info: DateSelectArg): EventEditorDraft {
  return {
    title: '',
    description: '',
    eventDate: formatDateKey(info.start),
    startTime: info.allDay ? '' : toTimeLabel(info.start),
    endTime: info.allDay || !info.end ? '' : toTimeLabel(info.end),
    allDay: info.allDay,
    color: '#3b82f6',
    taskId: null,
  };
}

function toInclusiveRange(info: DatesSetArg): { start: string; end: string } {
  const end = new Date(info.end);
  end.setDate(end.getDate() - 1);
  return {
    start: formatDateKey(info.start),
    end: formatDateKey(end),
  };
}

function toTimeLabel(date: Date): string {
  return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
}
